import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Turnstile } from '@/components/ui/turnstile';
import { useAuth } from '@/hooks/useAuth';
import { ArrowRight, Loader2, AlertCircle } from 'lucide-react';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

// Check if running on local network (where Turnstile may not work)
const isLocalNetwork =
  typeof window !== 'undefined' &&
  !['localhost', '127.0.0.1'].includes(window.location.hostname) &&
  (window.location.hostname.match(/^192\.168\./) ||
    window.location.hostname.match(/^10\./) ||
    window.location.hostname.match(/^172\.(1[6-9]|2[0-9]|3[01])\./));

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const { login, isLoggingIn, loginError } = useAuth();

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError(null);
  }, []);

  const handleTurnstileError = useCallback((error?: string) => {
    setTurnstileToken(null);
    setTurnstileError(error || 'Verification failed. Please try again.');
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError('Verification expired. Please verify again.');
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError(null);
    setTurnstileKey(prev => prev + 1);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Skip Turnstile check on local network (Turnstile doesn't work with local IPs)
    if (TURNSTILE_SITE_KEY && !turnstileToken && !isLocalNetwork) {
      setTurnstileError('Please complete the verification.');
      return;
    }

    try {
      await login(email, password, turnstileToken || undefined);
    } catch (error: unknown) {
      // Reset widget on Turnstile-related errors
      if (
        error instanceof Error &&
        (error.message.includes('TURNSTILE') || error.message.includes('Verification'))
      ) {
        resetTurnstile();
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-mesh">
      {/* Left Panel - Branding (Hidden on mobile, visible on lg+) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-primary relative overflow-hidden items-center justify-center">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="shadow-2xl inline-block rounded-lg">
              <img src="/logo.svg" alt="Muraka CRM" className="w-24 h-24 lg:w-32 lg:h-32" />
            </div>
          </div>

          {/* Login Card */}
          <div
            className="bg-card rounded-xl shadow-lg border border-border p-8"
            data-testid="login-card"
          >
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-foreground">Welcome back</h2>
              <p className="text-muted-foreground mt-2">
                Enter your credentials to access your account
              </p>
            </div>

            {/* Error Alert */}
            {loginError && (
              <div
                className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-3"
                data-testid="login-error"
              >
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Invalid credentials</p>
                  <p className="mt-1 text-destructive/80">
                    {loginError.message ||
                      'The email or password you entered is incorrect. Please try again.'}
                  </p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  data-testid="email-input"
                  className="h-11 px-4 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus-ring transition-all hover:border-primary/50"
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                  required
                  data-testid="password-input"
                  className="h-11 px-4 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus-ring transition-all hover:border-primary/50"
                />
              </div>

              {/* Turnstile Widget */}
              {TURNSTILE_SITE_KEY && (
                <div className="pt-2">
                  <Turnstile
                    key={turnstileKey}
                    siteKey={TURNSTILE_SITE_KEY}
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                    theme="auto"
                    className="flex justify-center"
                  />
                  {turnstileError && (
                    <p className="mt-2 text-sm text-destructive text-center">{turnstileError}</p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-11 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 focus-ring transition-all flex items-center justify-center gap-2"
                disabled={isLoggingIn || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                data-testid="login-submit"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
